import { useState, useEffect } from "react";
import axios from "axios";
import QRCode from "react-qr-code";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

import { Link } from "react-router-dom";

export default function AdminFundraisers() {
    const [funds, setFunds] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [showPast, setShowPast] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        title: "", description: "", goal: "", goalPublic: true, prices: [{ label: "", amount: "" }],
    });

    /* ---------- fetch ---------- */
    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const res = await axios.get("/api/fundraisers", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFunds(res.data);
        } catch (err) {
            console.error("Failed to load fundraisers:", err);
        }
    };

    /* ---------- form helpers ---------- */
    const addPrice = () =>
        setForm({ ...form, prices: [...form.prices, { label: "", amount: "" }] });

    const updatePrice = (i, f, val) => {
        const p = [...form.prices]; p[i][f] = val; setForm({ ...form, prices: p });
    };

    const create = async () => {
        if (saving) return;
        if (!form.title || !form.goal) return toast.warning("Title and goal required");
        if (form.prices.length < 1) return toast.warning("At least one price option is required");
        if (form.prices.some(p => !p.amount)) return toast.warning("Each row needs an amount");

        // prepare data
        const today = new Date().toLocaleDateString("en-US");           // e.g. 7/16/2025
        const titleWithDate = `${form.title.trim()} - ${today}`;

        const cleanedPrices = form.prices.map(p => {
            const amt = parseFloat(p.amount);
            const label = p.label.trim() || `$${amt} Donation`;
            return { label, amount_dollars: amt };
        });

        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.post("/api/fundraisers", {
                title: titleWithDate,
                description: form.description,
                goal_amount_dollars: parseFloat(form.goal),
                goal_public: form.goalPublic,
                prices: cleanedPrices,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForm({ title: "", description: "", goal: "", goalPublic: true, prices: [{ label: "", amount: "" }] });
            setShowForm(false);
            load();
        } finally { setSaving(false); }
    };

    /* ---------- archive toggles ---------- */
    const archiveFund = async id => {
        const ok = await confirmSwal({
            title: "Archive fundraiser?",
            text: "You can restore it later from Past Fundraisers.",
            icon: "question",
            confirmText: "Archive",
            confirmColor: "#d33",
        });
        if (!ok) return;
        const token = await auth.currentUser.getIdToken();
        await axios.patch(`/api/fundraisers/${id}/archive`, null, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        load();
    };
    
    const unarchiveFund = async (id) => {
        const token = await auth.currentUser.getIdToken();
        await axios.patch(`/api/fundraisers/${id}/unarchive`, null, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        load();
    };

    /* ---------- copy helper ---------- */
    const copyLink = (url, id) => { navigator.clipboard.writeText(url); setCopiedId(id); setTimeout(() => setCopiedId(null), 1500); };

    const active = funds.filter(f => !f.archived);
    const archived = funds.filter(f => f.archived);

    /* ---------- UI ---------- */
    return (
        <div className="container mt-2">
            <p>Fundraisers are pages created to accept donations based on fixed prices. If you would instead like to allow donators to set a custom amount, see Donations.</p>

            <div className="d-flex flex-column align-items-start gap-2 mb-3">
                <button
                    className={`btn ${showForm ? "btn-admin" : "btn-admin"}`}
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? "Cancel" : "Create New Fundraiser"}
                </button>
            </div>

            {showForm && (
                <div className="card p-3 mb-4">
                    <h5>Create Fundraiser</h5>
                    <input className="form-control mb-2" placeholder="Title"
                        value={form.title} disabled={saving}
                        onChange={e => setForm({ ...form, title: e.target.value })} />
                    <textarea className="form-control mb-2" placeholder="Description (Optional)"
                        value={form.description} disabled={saving}
                        onChange={e => setForm({ ...form, description: e.target.value })} />
                    <input type="number" className="form-control mb-2" placeholder="Goal (USD)"
                        value={form.goal} disabled={saving}
                        onChange={e => setForm({ ...form, goal: e.target.value })} />
                    <div className="form-check">
                        <input className="form-check-input mb-2" type="checkbox" checked={form.goalPublic} disabled={saving} id="goalPublic"
                            onChange={e => setForm({ ...form, goalPublic: e.target.checked })} />
                        <label className="form-check-label" htmlFor="goalPublic">
                            Publicly Show Goal & Donor Count
                        </label>
                    </div>

                    <h6>Price options</h6>
                    {form.prices.map((p, i) => (
                        <div key={i} className="d-flex gap-2 mb-2">
                            <input className="form-control" placeholder="Label (optional)"
                                value={p.label} disabled={saving}
                                onChange={e => updatePrice(i, "label", e.target.value)} />
                            <input type="number" className="form-control" placeholder="Amount ($)"
                                value={p.amount} disabled={saving}
                                onChange={e => updatePrice(i, "amount", e.target.value)} />
                            <button className="btn btn-outline-secondary" disabled={saving}
                                onClick={() => setForm({ ...form, prices: form.prices.filter((_, idx) => idx !== i) })}>×</button>
                        </div>
                    ))}
                    <button className="btn btn-custom-outline btn-sm mb-3" onClick={addPrice} disabled={saving}>+ Add price</button>
                    <button className="btn btn-admin" style={{ color: "white" }} onClick={create} disabled={saving}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</> : "Save"}
                    </button>
                </div>
            )}

            {active.map(f => (
                <FundraiserCard key={f._id} fund={f} archived={false}
                    copiedId={copiedId} copyLink={copyLink} onRefresh={load}
                    onArchive={() => archiveFund(f._id)}
                />
            ))}

            {archived.length > 0 && (
                <>
                    <hr className="my-4" />

                    <div className="d-flex flex-column align-items-start gap-2 mb-3">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPast(!showPast)}
                        >
                            {showPast ? "Hide" : "Show"} Past Fundraisers
                        </button>
                    </div>

                    {showPast &&
                        archived.map(f => (
                            <FundraiserCard
                                key={f._id}
                                fund={f}
                                archived
                                copiedId={copiedId}
                                copyLink={copyLink}
                                onRefresh={load}
                                onUnarchive={() => unarchiveFund(f._id)}
                            />
                        ))}
                </>
            )}
        </div>
    );
}

/* ---------- child card ---------- */
function FundraiserCard({
    fund: f,
    copyLink,
    copiedId,
    onRefresh,
    onArchive,
    onUnarchive,
    archived = false,
}) {
    const raised = f.total_raised_cents / 100;
    const goal = f.goal_amount_cents / 100;
    const pct = Math.min((raised / goal) * 100, 100);

    const [showDonors, setShowDonors] = useState(false);

    return (
        <div className="card mb-4 p-3">
            {/* header */}
            <div className="d-flex justify-content-between align-items-start mb-1">
                <h5 className="mb-0">{f.title}</h5>
                {archived ? (
                    <button
                        className="btn btn-sm btn-outline-secondary ms-3"
                        onClick={onUnarchive}
                    >
                        Unarchive
                    </button>
                ) : (
                    <button
                        className="btn btn-sm btn-outline-secondary ms-3"
                        onClick={onArchive}
                    >
                        Archive
                    </button>
                )}
            </div>

            {f.description && <p>{f.description}</p>}

            {/* progress */}
            <div className="progress mb-2" style={{ height: "20px" }}>
                <div
                    className="progress-bar bg-success"
                    style={{ width: `${pct}%` }}
                >
                    {pct.toFixed(0)}%
                </div>
            </div>

            <p className="d-flex align-items-center gap-2">
                ${raised.toFixed(2)} raised of ${goal.toFixed(2)}{!f.goal_public && (<small className="text-body-secondary">(Private)</small>)}
                <button
                    className="btn btn-sm btn-outline-secondary"
                    title="Refresh"
                    onClick={onRefresh}
                >
                    ↻
                </button>
            </p>

            {/* public link */}
            <h6 className="">Public Link</h6>
            {/* <div className="my-2">
                <QRCode value={`${window.location.origin}/fundraisers/${f._id}`} size={150} />
            </div> */}
            <div className="input-group" style={{ maxWidth: "450px" }}>
                <input
                    className="form-control form-control-sm"
                    readOnly
                    value={`${window.location.origin}/fundraisers/${f._id}`}
                    onClick={(e) => e.target.select()}
                />
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                        copyLink(
                            `${window.location.origin}/fundraisers/${f._id}`,
                            `public_${f._id}`
                        )
                    }
                >
                    {copiedId === `public_${f._id}` ? "Copied!" : "Copy"}
                </button>
            </div>

            {/* donation links */}
            <h6 className="mt-2">Direct Price Option Links</h6>
            <div className="row">
                {f.price_items.map((pi) => (
                    <div key={pi.payment_link_id} className="col-sm-6 col-md-4 mb-3">
                        <div className="border p-2 text-center h-100 d-flex flex-column">
                            <strong className="mb-0">{pi.label}</strong>
                            <small className="mb-0 mt-0">
                                ${(pi.amount_cents / 100).toFixed(2)}
                            </small>

                            {/* QR code */}
                            <div className="my-2 flex-grow-1 d-flex align-items-center justify-content-center">
                                <QRCode value={`${window.location.origin}/fundraisers/${f._id}/${pi.price_id}`} size={150} />
                            </div>

                            {/* copy‑to‑clipboard box */}
                            <div className="input-group mt-2">
                                <input
                                    className="form-control form-control-sm"
                                    readOnly
                                    value={`${window.location.origin}/fundraisers/${f._id}/${pi.price_id}`}
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => copyLink(`${window.location.origin}/fundraisers/${f._id}/${pi.price_id}`, pi.payment_link_id)}
                                >
                                    {copiedId === pi.payment_link_id ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ---- donor list toggle ---- */}
            <button
                className="btn small-link mt-2 p-0"
                style={{ color: "#2E5D3A" }}
                onClick={() => setShowDonors(!showDonors)}
            >
                {showDonors ? "Hide" : "Show"} Donors ({f.donor_count || 0})
            </button>

            {showDonors && (
                <div className="table-responsive mt-2">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Amount</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {f.donations && f.donations.length > 0 ? (
                                f.donations
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            new Date(b.timestamp) - new Date(a.timestamp)
                                    )
                                    .map((d, i) => (
                                        <tr key={i}>
                                            <td>{d.name}</td>
                                            <td>{d.email}</td>
                                            <td>${(d.amount_cents / 100).toFixed(2)}</td>
                                            <td>
                                                {new Date(d.timestamp).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-muted">
                                        No donations yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
